import DOMUtils from "@utils/dom.utils";
import { isDefined } from "@utils/var.check";

/**
 * @description Utilities class for working with sliders
 */
const SliderUtils = {
  /**
   * @summary Sets CSS variables for slider tick labels and margin
   * @param {HTMLElement | string} root - The root element or selector to search within
   * @example
   * SliderUtils.setSliderCSSVar(document.getElementById('root'))
   * SliderUtils.setSliderCSSVar('#root')
   */
  setSliderCSSVar: (root, calculatedWidth = null) => {
    if (!isDefined(root)) {
      return;
    }

    // Get the .sls-slider element
    const sliderElement = DOMUtils.elOf(root, ".sls-slider")[0];

    if (sliderElement) {
      // Get the width of the slider element
      const sliderWidth =
        calculatedWidth != null ? calculatedWidth : window.getComputedStyle(sliderElement).width;

      // Get the slider tick labels
      const sliderTickLabels = DOMUtils.elOf(root, ".sls-slider .v-slider-track__tick-label");

      if (sliderTickLabels && sliderTickLabels.length > 0) {
        // Calculate the number of slides
        const numberOfSlides = sliderTickLabels.length;

        // Calculate the width of each tick label
        const tickLabelWidth = Math.floor(
          parseFloat(sliderWidth) / (numberOfSlides / 2 + numberOfSlides),
        );

        // Set the CSS variable --tick-label-width to the calculated slide width
        DOMUtils.setCSSVariableInElement(
          sliderElement,
          "--tick-label-width",
          `${tickLabelWidth}px`,
        );

        // Calculate the maximum height of slider tick labels
        const sliderTickLabelsHeight = Array.from(sliderTickLabels).map(
          (label) => label.clientHeight,
        );

        // Set the CSS variable --slider-margin-bottom to the maximum height of slider tick labels
        DOMUtils.setCSSVariableInElement(
          sliderElement,
          "--slider-margin-bottom",
          `${Math.max(...sliderTickLabelsHeight)}px`,
        );
      }
    }
  },

  // SliderUtils.js

  /**
   * Creates an observer for a specific slider.
   * Returns the observer instance so the component can disconnect it later.
   */
  watchSliderWidth: (root, onWidthChange) => {
    let lastWidth = 0;
    let rafId: number | null = null;
    let observer: ResizeObserver | null = null;
    const startObserve = () => {
      if (!root) return null;

      const sliderEl = DOMUtils.elOf(root, ".sls-slider")[0];

      // 1. Handle Late Mount
      if (!sliderEl) {
        rafId = requestAnimationFrame(startObserve);
        return null;
      }

      // 2. Create the Observer
      observer = new ResizeObserver((entries) => {
        const newWidth = entries[0]?.contentRect.width;

        // Threshold check (0.5px) prevents jitter
        if (newWidth > 0 && Math.abs(newWidth - lastWidth) > 0.5) {
          lastWidth = newWidth;
          onWidthChange(newWidth);
        }
      });

      observer.observe(sliderEl);
      return observer;
    };

    // Start the process
    startObserve();

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId); // Now rafId is "read/used"!
      }
      if (observer) {
        observer.disconnect();
      }
    };
  },
};

export default SliderUtils;
